import React, { useState, useEffect, useCallback } from "react";
import useAuth from "../hooks/useAuth";
import api from "../services/api";
import { errorToUserMessage } from "../services/errorMessages";

export default function ExportCenter() {
  const { user } = useAuth({ requiredRole: "manager" });

  const [exports, setExports] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [selectedFormat, setSelectedFormat] = useState("csv");
  const [vanExportType, setVanExportType] = useState("survey_responses");
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);

  // VAN config state
  const [showVanConfig, setShowVanConfig] = useState(false);
  const [vanPreview, setVanPreview] = useState(null);
  const [vanMappings, setVanMappings] = useState([]);
  const [savingMappings, setSavingMappings] = useState(false);

  // Load initial data
  useEffect(() => {
    Promise.all([
      api.get("/exports").catch(() => ({ data: { exports: [] } })),
      api.get("/campaigns").catch(() => ({ data: { campaigns: [] } })),
    ]).then(([exportsRes, campaignsRes]) => {
      setExports(exportsRes.data.exports || []);
      setCampaigns(campaignsRes.data.campaigns || []);
    }).finally(() => setLoading(false));
  }, []);

  // Load VAN preview when campaign changes and format is VAN
  useEffect(() => {
    if (selectedFormat === "van" && selectedCampaign) {
      loadVanPreview();
    } else {
      setVanPreview(null);
    }
  }, [selectedCampaign, selectedFormat]); // eslint-disable-line

  const loadVanPreview = async () => {
    if (!selectedCampaign) return;
    try {
      const [previewRes, mappingsRes] = await Promise.all([
        api.get(`/exports/van-preview/${selectedCampaign}`),
        api.get(`/exports/van-mappings/${selectedCampaign}`),
      ]);
      setVanPreview(previewRes.data);

      // Initialize mapping form from existing or blank
      const existing = mappingsRes.data.mappings || [];
      if (existing.length > 0) {
        setVanMappings(existing);
      } else if (previewRes.data.tags) {
        setVanMappings(previewRes.data.tags.map((t) => ({
          survey_tag_id: t.id,
          tag_name: t.name,
          van_survey_question_id: "",
          van_survey_response_id: "",
          van_activist_code_id: "",
        })));
      }
    } catch (err) {
      console.error("Failed to load VAN preview:", err);
    }
  };

  const saveVanMappings = async () => {
    setSavingMappings(true);
    try {
      const validMappings = vanMappings.filter((m) => m.van_survey_question_id);
      await api.post(`/exports/van-mappings/${selectedCampaign}`, {
        mappings: validMappings,
      });
      alert("VAN mappings saved!");
    } catch (err) {
      alert(errorToUserMessage(err, "Failed to save mappings."));
    } finally {
      setSavingMappings(false);
    }
  };

  const requestExport = async () => {
    if (!selectedCampaign && selectedFormat === "van") {
      alert("Please select a campaign for VAN exports");
      return;
    }

    setRequesting(true);
    try {
      const res = await api.post("/exports", {
        campaign_id: selectedCampaign || null,
        format: selectedFormat,
        van_export_type: vanExportType,
      });
      setExports((prev) => [res.data, ...prev]);
    } catch (err) {
      alert(errorToUserMessage(err, "Export failed."));
    } finally {
      setRequesting(false);
    }
  };

  const handleDownload = async (exportItem) => {
    try {
      const res = await api.get(`/exports/${exportItem.id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `export_${exportItem.id}_${exportItem.format}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert("Download failed");
    }
  };

  const updateMapping = (idx, field, value) => {
    setVanMappings((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const statusColors = {
    pending: "bg-yellow-100 text-yellow-700",
    processing: "bg-blue-100 text-blue-700",
    completed: "bg-green-100 text-green-700",
    failed: "bg-red-100 text-red-700",
  };

  const formatDescriptions = {
    csv: "Standard CSV with all campaign data, messages, and survey tags",
    xlsx: "Excel spreadsheet with formatted columns",
    van: "VAN/VoteBuilder import file with survey responses and contact history",
    pdi: "PDI-compatible export format",
  };

  if (!user) return null;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Export Center</h1>

      {/* Export request form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="font-semibold text-gray-900 mb-4">Create Export</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div>
            <label className="block text-sm text-gray-600 mb-1">Campaign</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
            >
              <option value="">All Campaigns</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-600 mb-1">Format</label>
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={selectedFormat}
              onChange={(e) => setSelectedFormat(e.target.value)}
            >
              <option value="csv">CSV — Full Campaign Data</option>
              <option value="van">VAN/VoteBuilder</option>
              <option value="xlsx">Excel (XLSX)</option>
              <option value="pdi">PDI</option>
            </select>
          </div>
          {selectedFormat === "van" && (
            <div>
              <label className="block text-sm text-gray-600 mb-1">VAN File Type</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={vanExportType}
                onChange={(e) => setVanExportType(e.target.value)}
              >
                <option value="survey_responses">Survey Responses</option>
                <option value="contact_history">Contact History</option>
                <option value="activist_codes">Activist Codes</option>
                <option value="combined">All Files (Combined)</option>
              </select>
            </div>
          )}
          <div>
            <button
              onClick={requestExport}
              disabled={requesting}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300 transition-colors"
            >
              {requesting ? "Generating..." : "Generate Export"}
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          {formatDescriptions[selectedFormat]}
        </p>
      </div>

      {/* VAN Configuration Panel */}
      {selectedFormat === "van" && selectedCampaign && vanPreview && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="font-semibold text-gray-900">VAN Export Configuration</h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Map your survey tags to VAN SurveyQuestion and SurveyResponse IDs
              </p>
            </div>
            <button
              onClick={() => setShowVanConfig(!showVanConfig)}
              className="text-sm text-blue-600 font-medium hover:text-blue-800"
            >
              {showVanConfig ? "Hide Config" : "Configure Mappings"}
            </button>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-blue-700">{vanPreview.eligible_contacts}</p>
              <p className="text-xs text-blue-500">Contacts with VAN IDs</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-green-700">{vanPreview.mappings?.length || 0}</p>
              <p className="text-xs text-green-500">Mapped Tags</p>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 text-center">
              <p className="text-2xl font-bold text-amber-700">{vanPreview.unmapped_tags?.length || 0}</p>
              <p className="text-xs text-amber-500">Unmapped Tags</p>
            </div>
          </div>

          {vanPreview.unmapped_tags?.length > 0 && !showVanConfig && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-700">
              <strong>Unmapped tags:</strong> {vanPreview.unmapped_tags.join(", ")}.
              Configure mappings to include these in your VAN export.
            </div>
          )}

          {/* Mapping configuration */}
          {showVanConfig && (
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 uppercase px-2">
                <div className="col-span-3">Survey Tag</div>
                <div className="col-span-3">VAN Survey Question ID</div>
                <div className="col-span-3">VAN Survey Response ID</div>
                <div className="col-span-3">Activist Code ID (optional)</div>
              </div>

              {vanMappings.map((mapping, idx) => (
                <div key={mapping.survey_tag_id || idx} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-3">
                    <div className="flex items-center space-x-2 bg-gray-50 rounded-lg px-3 py-2">
                      <span className="text-sm font-medium text-gray-700">
                        {mapping.tag_name || vanPreview.tags?.find((t) => t.id === mapping.survey_tag_id)?.name || `Tag #${mapping.survey_tag_id}`}
                      </span>
                    </div>
                  </div>
                  <div className="col-span-3">
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                      placeholder="e.g., 385214"
                      value={mapping.van_survey_question_id || ""}
                      onChange={(e) => updateMapping(idx, "van_survey_question_id", e.target.value)}
                    />
                  </div>
                  <div className="col-span-3">
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                      placeholder="e.g., 1289453"
                      value={mapping.van_survey_response_id || ""}
                      onChange={(e) => updateMapping(idx, "van_survey_response_id", e.target.value)}
                    />
                  </div>
                  <div className="col-span-3">
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono"
                      placeholder="e.g., 4129533"
                      value={mapping.van_activist_code_id || ""}
                      onChange={(e) => updateMapping(idx, "van_activist_code_id", e.target.value)}
                    />
                  </div>
                </div>
              ))}

              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-gray-400">
                  Get these IDs from VAN &gt; Admin &gt; Survey Questions or your VAN committee admin
                </p>
                <button
                  onClick={saveVanMappings}
                  disabled={savingMappings}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  {savingMappings ? "Saving..." : "Save Mappings"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Export history */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-semibold text-gray-900">Export History</h2>
          <span className="text-xs text-gray-400">{exports.length} exports</span>
        </div>
        {loading ? (
          <div className="p-6 text-center text-gray-400">Loading...</div>
        ) : exports.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-400 text-sm">No exports yet</p>
            <p className="text-gray-300 text-xs mt-1">Create your first export above</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Campaign</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Format</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Rows</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {exports.map((exp) => {
                const campaign = campaigns.find((c) => c.id === exp.campaign_id);
                return (
                  <tr key={exp.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(exp.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {campaign?.name || "All"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium uppercase ${
                        exp.format === "van"
                          ? "bg-purple-100 text-purple-700"
                          : "bg-gray-100 text-gray-700"
                      }`}>
                        {exp.format}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[exp.status] || ""}`}>
                        {exp.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right">
                      {exp.row_count || "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {exp.status === "completed" && (
                        <button
                          onClick={() => handleDownload(exp)}
                          className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                        >
                          Download
                        </button>
                      )}
                      {exp.status === "failed" && (
                        <span className="text-xs text-red-500">Failed</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* VAN format reference */}
      {selectedFormat === "van" && (
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">VAN Export File Types</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-gray-500">
            <div>
              <p className="font-medium text-gray-700 mb-1">Survey Responses</p>
              <p>Maps tagged survey responses to VAN SurveyQuestionID and SurveyResponseID. Requires VAN mapping configuration above.</p>
            </div>
            <div>
              <p className="font-medium text-gray-700 mb-1">Contact History</p>
              <p>Logs that each contact was reached via text (ContactTypeID=70). Maps contact status to VAN CanvassResultIDs.</p>
            </div>
            <div>
              <p className="font-medium text-gray-700 mb-1">Activist Codes</p>
              <p>Applies VAN activist codes based on tag-to-code mappings. Requires activist code IDs in VAN config.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
